import {
  Box,
  Button,
  Chip,
  Divider,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  semanticTone,
  warningSoftStyles,
} from 'components/@home/@settings/Tabs/Agents/agenticThemeHelpers';
import { AgentEditContext } from 'components/@home/@settings/Tabs/Agents/AgentEditContext';
import {
  detachTool,
  isToolAttached,
  sanitizeAttachmentsForApi,
} from 'components/@home/@settings/Tabs/Agents/attachedToolsHelpers';
import { sanitizeKnowledgeAttachmentsForApi } from 'components/@home/@settings/Tabs/Agents/attachedKnowledgeHelpers';
import ChatFlowContextHeader from 'components/@home/@settings/Tabs/ChatFlowContextHeader';
import { ChatFlowContext } from 'components/@home/@settings/Tabs/ChatFlowContext';
import IdentityCard from 'components/@home/@settings/Tabs/Agents/IdentityCard';
import GeneralPanel from 'components/@home/@settings/Tabs/Agents/toolPanels/GeneralPanel';
import RequestPanel from 'components/@home/@settings/Tabs/Agents/toolPanels/RequestPanel';
import SchemePanel from 'components/@home/@settings/Tabs/Agents/toolPanels/SchemePanel';
import SecretsPanel from 'components/@home/@settings/Tabs/Agents/toolPanels/SecretsPanel';
import Agent from 'services/api/Agent';
import AgentSkill from 'services/api/AgentSkill';
import AgentTool from 'services/api/AgentTool';
import omit from 'lodash/omit';
import CircleSmallIcon from 'mdi-react/CircleSmallIcon';
import CogOutlineIcon from 'mdi-react/CogOutlineIcon';
import CodeBracesIcon from 'mdi-react/CodeBracesIcon';
import DotsHorizontalIcon from 'mdi-react/DotsHorizontalIcon';
import HomeOutlineIcon from 'mdi-react/HomeOutlineIcon';
import KeyOutlineIcon from 'mdi-react/KeyOutlineIcon';
import MinusIcon from 'mdi-react/MinusIcon';
import TrashCanOutlineIcon from 'mdi-react/TrashCanOutlineIcon';
import WebIcon from 'mdi-react/WebIcon';
import WrenchOutlineIcon from 'mdi-react/WrenchOutlineIcon';
import { bool, object, string } from 'prop-types';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import FadeInOut from 'components/common/FadeInOut';
import { useNavigate, useParams } from 'react-router';
import { buildAgenticPath } from 'utils/agenticRoute';
import agenticSectionPath from 'utils/agenticSectionPath';
import agenticSlug from 'utils/agenticSlug';
import { normalizeToolTrigger } from 'constants/agenticToolTriggers';
import connector from 'components/@home/@settings/Tabs/connector';
import { attachedSkillsForAgent } from 'utils/skillToolsUnion';
import { I18n } from 'utils/i18n';
import toolNameConflictMessage from 'utils/toolNameConflict';

const getStyles = theme => {
  const mutedChip = semanticTone(theme, 'default');
  return {
    root: {
      flex: '1 1 0',
      minHeight: 0,
      display: 'flex',
      backgroundColor: theme.palette.background.paper,
      flexDirection: 'column',
      overflow: 'hidden',
    },
    body: {
      flex: '1 1 0',
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    chrome: {
      flexShrink: 0,
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      paddingTop: theme.spacing(2),
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
    tabContent: {
      flex: '1 1 0',
      minHeight: 0,
      overflowY: 'auto',
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(3),
    },
    attachedPill: {
      height: 22,
      fontSize: theme.typography.pxToRem(13),
      fontWeight: 500,
      borderRadius: theme.shape.borderRadius,
      borderColor: theme.palette.divider,
      '& .MuiChip-icon': {
        color: theme.palette.warning.main,
        width: 40,
        height: 40,
        marginRight: '-16px',
        marginLeft: '-8px',
      },
    },
    attachedPillTooltip: {
      padding: 0,
    },
    kindPill: {
      height: 22,
      fontSize: theme.typography.pxToRem(11),
      fontWeight: 600,
      letterSpacing: '0.3px',
      backgroundColor: mutedChip.bg,
      color: mutedChip.fg,
      borderRadius: '4px',
      '& .MuiChip-label': { padding: '0 8px' },
    },
    identityPopoverFooter: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(0.75),
      paddingTop: theme.spacing(0.5),
      fontSize: theme.typography.pxToRem(12),
      color: theme.palette.text.secondary,
    },
    moreActionsButton: {
      borderColor: theme.palette.divider,
      minWidth: 0,
    },
    tabsRoot: {
      minHeight: 44,
      borderBottom: `1px solid ${theme.palette.divider}`,
      '& .MuiTabs-flexContainer': { gap: theme.spacing(2) },
    },
    tabRoot: {
      textTransform: 'none',
      minHeight: 44,
      minWidth: 'auto',
      padding: theme.spacing(1, 0.5),
      fontSize: theme.typography.pxToRem(13),
      fontWeight: 500,
      color: theme.palette.text.secondary,
      '&.Mui-selected': { color: theme.palette.text.primary, fontWeight: 600 },
    },
    tabIcon: {
      marginRight: theme.spacing(0.75),
      display: 'inline-flex',
      verticalAlign: 'middle',
    },
    footer: {
      flexShrink: 0,
      borderTop: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(2),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(2),
      backgroundColor: theme.palette.background.paper,
    },
    footerStatus: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
      minHeight: 32,
      flex: 1,
      minWidth: 0,
    },
    footerFeedback: {
      fontSize: theme.typography.pxToRem(13),
      fontWeight: 500,
    },
    dirtyChip: {
      height: 20,
      fontSize: theme.typography.pxToRem(10.5),
      fontWeight: 600,
      letterSpacing: '0.3px',
      textTransform: 'uppercase',
      ...warningSoftStyles(theme),
      border: 'none',
    },
    footerActions: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      flexShrink: 0,
    },
  };
};

const TAB_GENERAL = 'general';
const TAB_SCHEME = 'scheme';
const TAB_REQUEST = 'request';
const TAB_SECRETS = 'secrets';

const stripClientKeys = list => (list || []).map(p => omit(p, 'clientKey'));

const buildPayload = current => ({
  ...current,
  parameters: stripClientKeys(current.parameters),
  outputs: stripClientKeys(current.outputs),
});

const serializeToolPayload = current => JSON.stringify(buildPayload(current));

const normalizeTool = fetched => ({
  ...fetched,
  trigger: normalizeToolTrigger(fetched.trigger),
  http:
    fetched.kind === 'http'
      ? {
          method: 'POST',
          urlTemplate: '',
          headersTemplate: {},
          bodyTemplate: '',
          outputMapping: {},
          ...(fetched.http || {}),
        }
      : fetched.http,
  announcement: {
    whenToAnnounce: 'never',
    ...(fetched.announcement || {}),
  },
  longActionNotice: {
    enabled: false,
    waitSeconds: 0,
    ...(fetched.longActionNotice || {}),
  },
  secretNames: fetched.secretNames || [],
  outputs: (fetched.outputs || []).map(p => ({
    ...p,
    clientKey: p.clientKey || crypto.randomUUID(),
  })),
  parameters: (fetched.parameters || []).map(p => ({
    ...p,
    clientKey: p.clientKey || crypto.randomUUID(),
  })),
});

const ToolEditor = ({ toolId: toolIdProp = null, authCompany = null, embedded = false }) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const id = toolIdProp || routeId;
  const {
    context,
    selectedFlowId,
    chatFlows: flowsMap,
    onBackToList,
  } = useContext(ChatFlowContext);
  const parentFlow = selectedFlowId ? flowsMap?.[selectedFlowId] : null;
  const agentEdit = useContext(AgentEditContext);
  const [tool, setTool] = useState(null);
  const [agents, setAgents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState(TAB_GENERAL);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [identityAnchor, setIdentityAnchor] = useState(null);
  const [draftName, setDraftName] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [savingIdentity, setSavingIdentity] = useState(false);
  const [savedPayload, setSavedPayload] = useState(null);
  const [saveFeedback, setSaveFeedback] = useState(null);
  const saveFeedbackTimeoutRef = useRef(null);

  const clearSaveFeedbackLater = useCallback(() => {
    if (saveFeedbackTimeoutRef.current) {
      clearTimeout(saveFeedbackTimeoutRef.current);
    }
    saveFeedbackTimeoutRef.current = setTimeout(() => {
      setSaveFeedback(null);
      saveFeedbackTimeoutRef.current = null;
    }, 3000);
  }, []);

  useEffect(
    () => () => {
      if (saveFeedbackTimeoutRef.current) {
        clearTimeout(saveFeedbackTimeoutRef.current);
      }
    },
    [],
  );

  const dirty = useMemo(() => {
    if (!tool || savedPayload == null) return false;
    return serializeToolPayload(tool) !== savedPayload;
  }, [tool, savedPayload]);

  useEffect(() => {
    if (dirty && saveFeedback?.severity === 'success') {
      setSaveFeedback(null);
    }
  }, [dirty, saveFeedback?.severity]);

  useEffect(() => {
    if (tool?.kind !== 'http' && tab === TAB_REQUEST) {
      setTab(TAB_GENERAL);
    }
  }, [tool?.kind, tab]);

  useEffect(() => {
    if (!id) return;
    Promise.all([AgentTool.get(id), Agent.list()]).then(([fetched, fetchedAgents]) => {
      const normalized = normalizeTool(fetched);
      setTool(normalized);
      setSavedPayload(serializeToolPayload(normalized));
      setSaveFeedback(null);
      setAgents(fetchedAgents || []);
    });
  }, [id]);

  const attachedAgentsOther = useMemo(() => {
    if (!tool) return [];
    const tid = String(tool._id);
    const catalogSkills = agentEdit?.skills || [];
    return agents.filter(a => {
      if (a._id === agentEdit?.agent?._id) return false;
      const skillIds = (a.skillIds || a.draft?.skillIds || []).map(String);
      return catalogSkills.some(
        s =>
          skillIds.includes(String(s._id)) &&
          (s.attachedTools || []).some(att => att.tool && String(att.tool) === tid),
      );
    });
  }, [agents, tool, agentEdit?.agent?._id, agentEdit?.skills]);

  const attachedAgentCountOther = attachedAgentsOther.length;

  const attachedSkills = useMemo(
    () => attachedSkillsForAgent(agentEdit?.agent, agentEdit?.skills || []),
    [agentEdit?.agent, agentEdit?.skills],
  );

  const skillsWithTool = useMemo(
    () => attachedSkills.filter(skill => isToolAttached(skill.attachedTools, id)),
    [attachedSkills, id],
  );

  const onAgentGlobal = useMemo(
    () => isToolAttached(agentEdit?.agent?.attachedTools, id),
    [agentEdit?.agent?.attachedTools, id],
  );

  const canDetach = Boolean(
    embedded && agentEdit?.onSaveAgent && (skillsWithTool.length > 0 || onAgentGlobal),
  );

  const applySavedTool = useCallback(normalized => {
    setTool(normalized);
    setSavedPayload(serializeToolPayload(normalized));
  }, []);

  const save = async () => {
    setSaving(true);
    setSaveFeedback(null);
    try {
      const updated = await AgentTool.update(id, buildPayload(tool));
      const normalized = normalizeTool(updated);
      applySavedTool(normalized);
      setSaveFeedback({
        severity: 'success',
        message: I18n.t('AgenticChatFlow.ToolSaved'),
      });
      clearSaveFeedbackLater();
    } catch (err) {
      setSaveFeedback({
        severity: 'error',
        message: toolNameConflictMessage(err),
      });
      clearSaveFeedbackLater();
    } finally {
      setSaving(false);
    }
  };

  const saveIdentity = async () => {
    setSavingIdentity(true);
    try {
      const slug = agenticSlug(draftName) || tool.name;
      const updated = await AgentTool.update(
        id,
        buildPayload({
          ...tool,
          name: slug,
          description: draftDescription,
        }),
      );
      const normalized = normalizeTool(updated);
      applySavedTool(normalized);
      setSaveFeedback({
        severity: 'success',
        message: I18n.t('AgenticChatFlow.ToolSaved'),
      });
      clearSaveFeedbackLater();
      setIdentityAnchor(null);
    } catch (err) {
      setSaveFeedback({
        severity: 'error',
        message: toolNameConflictMessage(err),
      });
      clearSaveFeedbackLater();
    } finally {
      setSavingIdentity(false);
    }
  };

  const handleDelete = async () => {
    setMenuAnchor(null);
    await AgentTool.destroy(id);
    if (embedded && agentEdit?.flow?._id && agentEdit?.authCompany) {
      navigate(
        buildAgenticPath(agentEdit.authCompany, {
          flowId: agentEdit.flow._id,
          section: 'tools',
        }),
      );
    } else if (context?.pop) context.pop();
    else navigate(agenticSectionPath(authCompany));
  };

  const handleDetach = async () => {
    setMenuAnchor(null);
    if (!canDetach || !id) return;
    if (onAgentGlobal) {
      await agentEdit.onSaveAgent({
        attachedTools: detachTool(agentEdit.agent.attachedTools || [], id),
      });
    }
    await Promise.all(
      skillsWithTool.map(skill =>
        AgentSkill.update(skill._id, {
          displayName: skill.displayName,
          description: skill.description,
          attachedTools: sanitizeAttachmentsForApi(detachTool(skill.attachedTools || [], id)),
          attachedKnowledge: sanitizeKnowledgeAttachmentsForApi(skill.attachedKnowledge || []),
        }),
      ),
    );
    await agentEdit.onReloadSkills?.();
    if (agentEdit?.flow?._id && agentEdit?.authCompany) {
      navigate(
        buildAgenticPath(agentEdit.authCompany, {
          flowId: agentEdit.flow._id,
          section: 'tools',
        }),
      );
    }
  };

  const contextBreadcrumbs = useMemo(() => {
    if (!tool) return null;
    const items = [
      {
        key: 'home',
        icon: <HomeOutlineIcon size={14} />,
        onClick: onBackToList || context?.clear,
        title: I18n.t('AgenticChatFlow.AgentsTitle'),
        ariaLabel: I18n.t('AgenticChatFlow.AgentsTitle'),
      },
    ];
    if (parentFlow) {
      items.push({
        key: 'agent',
        label: parentFlow.name,
        onClick: context?.clear,
      });
      items.push({
        key: 'tools',
        label: I18n.t('AgenticChatFlow.NavTools'),
        onClick: context?.pop,
      });
    } else if (context?.pop) {
      items.push({
        key: 'tools',
        label: I18n.t('AgenticChatFlow.Tools'),
        onClick: context.pop,
      });
    }
    items.push({
      key: 'current',
      label: tool.name,
    });
    return items;
  }, [tool, context, parentFlow, onBackToList]);

  if (!tool) return null;

  const handleIdentityOpen = event => {
    setDraftName(tool.name || '');
    setDraftDescription(tool.description || '');
    setIdentityAnchor(event.currentTarget);
  };

  const closeIdentityPopover = () => {
    if (!savingIdentity) setIdentityAnchor(null);
  };

  const popoverAttachedLabel = attachedAgentsOther.map(a => a.name).join(', ');

  const kindLabel =
    tool.kind === 'http' ? I18n.t('AgenticChatFlow.KindHttp') : I18n.t('AgenticChatFlow.KindCode');

  return (
    <Box sx={[styles.root, embedded && { flex: '1 1 0', minHeight: 0, borderLeft: 'none' }]}>
      {!embedded && <ChatFlowContextHeader breadcrumbs={contextBreadcrumbs} />}
      <Box component="section" sx={styles.body}>
        <Box sx={styles.chrome}>
          <IdentityCard
            icon={<WrenchOutlineIcon size={22} />}
            title={tool.name}
            pill={
              <>
                <Chip size="small" sx={styles.kindPill} label={kindLabel} />
                {attachedAgentCountOther > 0 && (
                  <Tooltip
                    sx={styles.attachedPillTooltip}
                    title={popoverAttachedLabel}
                    placement="top"
                    arrow
                  >
                    <Chip
                      variant="outlined"
                      color="secondary"
                      size="small"
                      sx={styles.attachedPill}
                      icon={<CircleSmallIcon />}
                      label={I18n.t('AgenticChatFlow.AttachedToAgentsOther', {
                        count: attachedAgentCountOther,
                      })}
                    />
                  </Tooltip>
                )}
              </>
            }
            description={tool.description}
            emptyDescription={I18n.t('AgenticChatFlow.NoDescription')}
            edit={{
              open: Boolean(identityAnchor),
              anchorEl: identityAnchor,
              onOpen: handleIdentityOpen,
              onClose: closeIdentityPopover,
              content: (
                <>
                  <TextField
                    value={draftName}
                    onChange={e => setDraftName(e.target.value)}
                    size="small"
                    fullWidth
                    autoFocus
                    placeholder={I18n.t('AgenticChatFlow.Name')}
                  />
                  <TextField
                    value={draftDescription}
                    onChange={e => setDraftDescription(e.target.value)}
                    size="small"
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder={I18n.t('AgenticChatFlow.Description')}
                  />
                  <Box sx={styles.identityPopoverFooter}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {I18n.t('AgenticChatFlow.Kind')}
                    </Typography>
                    <Chip size="small" sx={styles.kindPill} label={kindLabel} />
                  </Box>
                  {popoverAttachedLabel && (
                    <Box sx={styles.identityPopoverFooter}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        {I18n.t('AgenticChatFlow.AttachedTo')}
                      </Typography>
                      <Typography
                        variant="caption"
                        noWrap
                        sx={{
                          color: 'text.primary',
                        }}
                      >
                        {popoverAttachedLabel}
                      </Typography>
                    </Box>
                  )}
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      justifyContent: 'flex-end',
                    }}
                  >
                    <Button
                      onClick={closeIdentityPopover}
                      disabled={savingIdentity}
                      color="inherit"
                    >
                      {I18n.t('AgenticChatFlow.Cancel')}
                    </Button>
                    <Button
                      onClick={saveIdentity}
                      variant="contained"
                      color="primary"
                      disableElevation
                      disabled={savingIdentity || !draftName.trim()}
                    >
                      {I18n.t('AgenticChatFlow.Save')}
                    </Button>
                  </Stack>
                </>
              ),
            }}
            actions={
              <>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={e => setMenuAnchor(e.currentTarget)}
                  size="small"
                  sx={styles.moreActionsButton}
                >
                  <DotsHorizontalIcon size={20} />
                </Button>
                <Menu
                  anchorEl={menuAnchor}
                  open={Boolean(menuAnchor)}
                  onClose={() => setMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  {canDetach && (
                    <MenuItem onClick={handleDetach}>
                      <MinusIcon size={16} style={{ marginRight: 8 }} />
                      {I18n.t('AgenticChatFlow.Detach')}
                    </MenuItem>
                  )}
                  {canDetach && <Divider />}
                  <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
                    <TrashCanOutlineIcon size={16} style={{ marginRight: 8 }} />
                    {I18n.t('AgenticChatFlow.Delete')}
                  </MenuItem>
                </Menu>
              </>
            }
          />

          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={styles.tabsRoot}
            TabIndicatorProps={{ sx: { height: 2 } }}
          >
            <Tab
              sx={styles.tabRoot}
              value={TAB_GENERAL}
              label={
                <>
                  <Box component="span" sx={styles.tabIcon}>
                    <CogOutlineIcon size={16} />
                  </Box>
                  {I18n.t('AgenticChatFlow.TabGeneral')}
                </>
              }
            />
            <Tab
              sx={styles.tabRoot}
              value={TAB_SCHEME}
              label={
                <>
                  <Box component="span" sx={styles.tabIcon}>
                    <CodeBracesIcon size={16} />
                  </Box>
                  {I18n.t('AgenticChatFlow.TabScheme')}
                </>
              }
            />
            {tool.kind === 'http' && (
              <Tab
                sx={styles.tabRoot}
                value={TAB_REQUEST}
                label={
                  <>
                    <Box component="span" sx={styles.tabIcon}>
                      <WebIcon size={16} />
                    </Box>
                    {I18n.t('AgenticChatFlow.TabRequest')}
                  </>
                }
              />
            )}
            <Tab
              sx={styles.tabRoot}
              value={TAB_SECRETS}
              label={
                <>
                  <Box component="span" sx={styles.tabIcon}>
                    <KeyOutlineIcon size={16} />
                  </Box>
                  {I18n.t('AgenticChatFlow.TabSecrets')}
                </>
              }
            />
          </Tabs>
        </Box>

        <Box sx={styles.tabContent}>
          {tab === TAB_GENERAL && <GeneralPanel tool={tool} setTool={setTool} />}
          {tab === TAB_SCHEME && <SchemePanel tool={tool} setTool={setTool} />}
          {tool.kind === 'http' && tab === TAB_REQUEST && (
            <RequestPanel tool={tool} setTool={setTool} />
          )}
          {tab === TAB_SECRETS && <SecretsPanel tool={tool} setTool={setTool} />}
        </Box>
      </Box>
      <Box component="footer" sx={styles.footer}>
        <Box sx={styles.footerStatus}>
          {dirty && (
            <Chip
              size="small"
              label={I18n.t('AgenticChatFlow.UnsavedChanges')}
              sx={styles.dirtyChip}
            />
          )}
          <FadeInOut show={Boolean(saveFeedback?.message)}>
            <Typography
              sx={[
                styles.footerFeedback,
                {
                  color: saveFeedback?.severity === 'error' ? 'error.main' : 'success.main',
                },
              ]}
            >
              {saveFeedback?.message}
            </Typography>
          </FadeInOut>
        </Box>
        <Box sx={styles.footerActions}>
          <Button
            variant="contained"
            color="primary"
            onClick={save}
            disabled={saving || !dirty}
            disableElevation
            sx={{ textTransform: 'none', borderRadius: '5px' }}
          >
            {I18n.t('AgenticChatFlow.Save')}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

ToolEditor.propTypes = {
  toolId: string,
  authCompany: object,
  embedded: bool,
};

export default connector(ToolEditor);
